import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetTenants,
  CreateTenant,
  GetTenantById,
  UpdateTenantStatus,
  UpdateTenant,
  GetTenantSubFeatures,
  UpdateTenantSubFeatures,
  AssignTenantPackage,
  UnassignTenantPackage,
  GetTenantPackage,
  GetTenantPackageHistory,
  AddTenantPackageCharge,
  GetTenantPackageCharges,
  DeleteTenantPackageCharge,
  GetTenantBilling,
  GetTenantPayments,
  RecordTenantPayment,
  TerminateTenant,
  ReactivateTenant,
  GetTenantsBillingOverview,
  GetTenantTokens,
  UpdateTenantTokens,
  GetTenantTokenHistory,
  GetTenantTokenUsage,
  RechargeTenantTokens,
  GetTenantDashboardStats,
  GetTenantSystemCredentials,
} from "../Api/tenant_api";
import { unwrapApiResponse } from "./query_helpers";
import { ApiResponse } from "../Types";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

const TENANTS_QUERY_KEY = ["tenants"];

const invalidateTenantQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY });
};

export const useTenantsQuery = (statusFilter?: string) =>
  useQuery({
    queryKey: [...TENANTS_QUERY_KEY, statusFilter],
    queryFn: async () =>
      unwrapApiResponse(
        await GetTenants(
          statusFilter ? { status_filter: statusFilter } : undefined,
        ),
      ),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useTenantQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [...TENANTS_QUERY_KEY, id],
    queryFn: async () => unwrapApiResponse(await GetTenantById(id!)),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useTenantSubFeaturesQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [...TENANTS_QUERY_KEY, id, "sub-features"],
    queryFn: async () => unwrapApiResponse(await GetTenantSubFeatures(id!)),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useCreateTenantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      name: string;
      tenant_sub_name: string;
      username: string;
      email: string;
      password: string;
      package_id?: string;
    }) => CreateTenant(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        invalidateTenantQueries(queryClient);
      }
    },
  });
};

export const useToggleTenantStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; status: string }) =>
      UpdateTenantStatus(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        invalidateTenantQueries(queryClient);
      }
    },
  });
};

export const useUpdateTenantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      id: string;
      name?: string;
      tenant_sub_name?: string;
    }) => UpdateTenant(payload),
    onSuccess: (res: ApiResponse) => {
      if (res?.success) {
        invalidateTenantQueries(queryClient);
      }
    },
  });
};

export const useUpdateTenantSubFeaturesMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      id: string;
      sub_feature_id: string;
      is_enable: number;
    }) => UpdateTenantSubFeatures(payload),
    onSuccess: (
      res: ApiResponse,
      variables: { id: string; sub_feature_id: string; is_enable: number },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANTS_QUERY_KEY, variables.id, "sub-features"],
        });
      }
    },
  });
};

const TENANT_PACKAGE_QUERY_KEY = ["tenant-package"];

export const useTenantPackageQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_PACKAGE_QUERY_KEY, tenantId],
    queryFn: async () => unwrapApiResponse(await GetTenantPackage(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useTenantPackageHistoryQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_PACKAGE_QUERY_KEY, tenantId, "history"],
    queryFn: async () =>
      unwrapApiResponse(await GetTenantPackageHistory(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useAssignTenantPackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      package_id: string;
      note?: string;
    }) => AssignTenantPackage(payload),
    onSuccess: (
      res: ApiResponse,
      variables: { tenant_id: string; package_id: string; note?: string },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_PACKAGE_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [...TENANTS_QUERY_KEY, variables.tenant_id, "sub-features"],
        });
        queryClient.invalidateQueries({
          queryKey: [...TENANT_BILLING_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

export const useUnassignTenantPackageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => UnassignTenantPackage(tenantId),
    onSuccess: (res: ApiResponse, tenantId: string) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_PACKAGE_QUERY_KEY, tenantId],
        });
      }
    },
  });
};

export const useTenantPackageChargesQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_PACKAGE_QUERY_KEY, tenantId, "charges"],
    queryFn: async () =>
      unwrapApiResponse(await GetTenantPackageCharges(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useAddTenantPackageChargeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      amount: number;
      description: string;
    }) => AddTenantPackageCharge(payload),
    onSuccess: (
      res: ApiResponse,
      variables: { tenant_id: string; amount: number; description: string },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_PACKAGE_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

export const useDeleteTenantPackageChargeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { tenant_id: string; charge_id: string }) =>
      DeleteTenantPackageCharge(payload),
    onSuccess: (
      res: ApiResponse,
      variables: { tenant_id: string; charge_id: string },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_PACKAGE_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

const TENANT_BILLING_QUERY_KEY = ["tenant-billing"];

export const useTenantBillingQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_BILLING_QUERY_KEY, tenantId],
    queryFn: async () => unwrapApiResponse(await GetTenantBilling(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useTenantPaymentsQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_BILLING_QUERY_KEY, tenantId, "payments"],
    queryFn: async () => unwrapApiResponse(await GetTenantPayments(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useRecordTenantPaymentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      amount: number;
      payment_method?: string;
      reference?: string;
      note?: string;
    }) => RecordTenantPayment(payload),
    onSuccess: (
      res: ApiResponse,
      variables: {
        tenant_id: string;
        amount: number;
        payment_method?: string;
        reference?: string;
        note?: string;
      },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_BILLING_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [...TENANTS_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

export const useTerminateTenantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { tenant_id: string; reason?: string }) =>
      TerminateTenant(payload),
    onSuccess: (res: ApiResponse, variables: { tenant_id: string }) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_BILLING_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [...TENANTS_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

export const useReactivateTenantMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      payment?: {
        amount: number;
        payment_method?: string;
        reference?: string;
        note?: string;
      };
    }) => ReactivateTenant(payload),
    onSuccess: (
      res: ApiResponse,
      variables: {
        tenant_id: string;
        payment?: {
          amount: number;
          payment_method?: string;
          reference?: string;
          note?: string;
        };
      },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_BILLING_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [...TENANTS_QUERY_KEY, variables.tenant_id],
        });
      }
    },
  });
};

export const useTenantsBillingOverviewQuery = () =>
  useQuery({
    queryKey: [...TENANT_BILLING_QUERY_KEY, "overview"],
    queryFn: async () => unwrapApiResponse(await GetTenantsBillingOverview()),
    staleTime: DEFAULT_STALE_TIME,
  });

const TENANT_TOKENS_QUERY_KEY = ["tenant-tokens"];

export const useTenantTokensQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_TOKENS_QUERY_KEY, tenantId],
    queryFn: async () => unwrapApiResponse(await GetTenantTokens(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useUpdateTenantTokensMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      token_type: "gemini" | "whatsapp";
      limit: number;
      reason: string;
    }) => UpdateTenantTokens(payload),
    onSuccess: (
      res: ApiResponse,
      variables: {
        tenant_id: string;
        token_type: "gemini" | "whatsapp";
        limit: number;
        reason: string;
      },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_TOKENS_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...TENANT_TOKENS_QUERY_KEY,
            variables.tenant_id,
            "history",
          ],
        });
      }
    },
  });
};

export const useTenantTokenHistoryQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_TOKENS_QUERY_KEY, tenantId, "history"],
    queryFn: async () =>
      unwrapApiResponse(await GetTenantTokenHistory(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useTenantTokenUsageQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: [...TENANT_TOKENS_QUERY_KEY, tenantId, "usage"],
    queryFn: async () =>
      unwrapApiResponse(await GetTenantTokenUsage(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useRechargeTenantTokensMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      tenant_id: string;
      token_type: "gemini" | "whatsapp";
      amount: number;
      reason: string;
    }) => RechargeTenantTokens(payload),
    onSuccess: (
      res: ApiResponse,
      variables: {
        tenant_id: string;
        token_type: "gemini" | "whatsapp";
        amount: number;
        reason: string;
      },
    ) => {
      if (res?.success) {
        queryClient.invalidateQueries({
          queryKey: [...TENANT_TOKENS_QUERY_KEY, variables.tenant_id],
        });
        queryClient.invalidateQueries({
          queryKey: [
            ...TENANT_TOKENS_QUERY_KEY,
            variables.tenant_id,
            "history",
          ],
        });
      }
    },
  });
};

export const useTenantDashboardStatsQuery = (tenantId: string | undefined) =>
  useQuery({
    queryKey: ["tenant-tokens", tenantId, "dashboard-stats"],
    queryFn: async () =>
      unwrapApiResponse(await GetTenantDashboardStats(tenantId!)),
    enabled: !!tenantId,
    staleTime: DEFAULT_STALE_TIME,
  });

export const useGetTenantSystemCredentialsMutation = () =>
  useMutation({
    mutationFn: (tenantId: string) => GetTenantSystemCredentials(tenantId),
  });
